import { EntryEditError } from '../edits.ts';
import { javaFixed } from '../javaText.ts';
import { int, scalar, transaction, type SqlConnection } from '../sql.ts';
import type { GridType, MapGrid, MapScale } from './atlas.ts';

/**
 * A map's scale, units and grid (docs/PARITY-STEP-4-PLAN.md section 6), ported from the
 * desktop's `MapScale`, `Units` and `MapGridGeometry` and checked against
 * `schema/fixtures/map-measure-cases.json`:
 * - a scale is how many of its unit one image pixel covers; 0 is no scale, and the ruler
 *   then measures in pixels;
 * - distances and areas show in each map's own unit, or converted to metric or imperial
 *   (Settings > Measurement units) when the unit is a real-world one;
 * - a grid is square cells, or pointy-top hexes in offset rows, `cellSize` image pixels
 *   across; past 40,000 cells nothing is drawn.
 */

export type UnitSystem = 'map' | 'metric' | 'imperial';

/** `MapScale.formatNumber`: whole and grouped from 100, one place from 10, two below; no trailing zeros. */
export function formatMeasure(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 100) return javaFixed(value, 0, true);
  if (abs >= 10) return javaFixed(value, 1).replace(/\.0$/, '');
  return javaFixed(value, 2).replace(/0+$/, '').replace(/\.$/, '');
}

export function formatDistance(distance: number, unit: string | null): string {
  return formatMeasure(distance) + (unit === null || unit.trim() === '' ? '' : ` ${unit.trim()}`);
}

/** `MapScale.format`: "142 miles", or "380 px" for a map with no scale. */
export function formatScaled(scale: MapScale, pixelLength: number): string {
  if (!(scale.unitsPerPixel > 0)) return `${formatMeasure(pixelLength)} px`;
  return formatMeasure(pixelLength * scale.unitsPerPixel) + (scale.unit === '' ? '' : ` ${scale.unit}`);
}

/** The largest 1, 2 or 5 times a power of ten not over `max`: what a scale bar shows (`niceDistance`). */
export function niceDistance(max: number): number {
  if (!(max > 0) || !Number.isFinite(max)) return 0;
  const magnitude = 10 ** Math.floor(Math.log10(max));
  for (const step of [5, 2, 1]) if (step * magnitude <= max * (1 + 1e-9)) return step * magnitude;
  return magnitude;
}

// ---- units (`Units`) ----

const METERS_PER_MILE = 1609.344;
const METERS_PER_FOOT = 0.3048;
const METERS: Readonly<Record<string, number>> = {
  mile: METERS_PER_MILE, miles: METERS_PER_MILE, mi: METERS_PER_MILE,
  foot: METERS_PER_FOOT, feet: METERS_PER_FOOT, ft: METERS_PER_FOOT,
  yard: 0.9144, yards: 0.9144, yd: 0.9144, yds: 0.9144,
  inch: 0.0254, inches: 0.0254,
  km: 1000, kms: 1000, kilometer: 1000, kilometers: 1000, kilometre: 1000, kilometres: 1000,
  m: 1, meter: 1, meters: 1, metre: 1, metres: 1,
  cm: 0.01, centimeter: 0.01, centimeters: 0.01, centimetre: 0.01, centimetres: 0.01,
};

/** How many metres one `unit` is, for the real-world units the desktop knows. */
export function metersPer(unit: string | null): number | null {
  if (unit === null) return null;
  return METERS[unit.trim().toLowerCase().replace(/\.$/, '')] ?? null;
}

export interface UnitDisplay {
  readonly unit: string;
  /** What to multiply a length in the map's unit by. */
  readonly factor: number;
  readonly converted: boolean;
}

/** The unit a length of about `typical` `unit`s is best shown in (`Units.display`). */
export function unitDisplay(typical: number, unit: string | null, system: UnitSystem): UnitDisplay {
  const meters = metersPer(unit);
  if (meters === null || system === 'map') return { unit: (unit ?? '').trim(), factor: 1, converted: false };
  const inMeters = Math.abs(typical) * meters;
  if (system === 'metric') return inMeters >= 1000 ? { unit: 'km', factor: meters / 1000, converted: true } : { unit: 'm', factor: meters, converted: true };
  return inMeters >= METERS_PER_MILE ? { unit: 'miles', factor: meters / METERS_PER_MILE, converted: true } : { unit: 'ft', factor: meters / METERS_PER_FOOT, converted: true };
}

/** A value already in `display`'s unit: "1 mile" but "2 miles" (`Units.format`). */
export function formatInUnit(value: number, display: UnitDisplay): string {
  const unit = display.converted && display.unit === 'miles' && formatMeasure(value) === '1' ? 'mile' : display.unit;
  return formatDistance(value, unit);
}

export function formatLength(distance: number, unit: string | null, system: UnitSystem): string {
  const display = unitDisplay(distance, unit, system);
  return formatInUnit(distance * display.factor, display);
}

export function formatArea(value: number, unit: string | null, system: UnitSystem): string {
  const meters = metersPer(unit);
  if (meters === null || system === 'map') return formatDistance(value, `sq ${(unit ?? '').trim()}`).replace('sq  ', 'sq ').trim();
  const square = Math.abs(value) * meters * meters;
  const sign = Math.sign(value);
  if (system === 'metric') return square >= 1_000_000 ? formatDistance((sign * square) / 1_000_000, 'sq km') : formatDistance(sign * square, 'sq m');
  const squareMile = METERS_PER_MILE * METERS_PER_MILE;
  return square >= squareMile / 10 ? formatDistance((sign * square) / squareMile, 'sq miles') : formatDistance((sign * square) / (METERS_PER_FOOT * METERS_PER_FOOT), 'sq ft');
}

/** A length of `pixels` on the map, as the Atlas shows it (`distanceText`). */
export function mapDistance(scale: MapScale, pixels: number, system: UnitSystem): string {
  return scale.unitsPerPixel > 0 ? formatLength(pixels * scale.unitsPerPixel, scale.unit, system) : formatScaled(scale, pixels);
}

// ---- the grid (`MapGridGeometry`) ----

/** Past this many cells the grid would be noise: nothing is drawn. */
export const MAX_GRID_CELLS = 40_000;
export const MIN_CELL_SIZE = 4;

export type Segment = readonly [number, number, number, number];

export function gridSegments(grid: MapGrid, width: number, height: number): Segment[] {
  if (grid.type === 'NONE' || width <= 0 || height <= 0) return [];
  return grid.type === 'HEX' ? hexSegments(grid.cellSize, width, height) : squareSegments(grid.cellSize, width, height);
}

function squareSegments(size: number, width: number, height: number): Segment[] {
  const segments: Segment[] = [];
  if ((width / size) * (height / size) > MAX_GRID_CELLS) return segments;
  for (let x = size; x < width; x += size) segments.push([x, 0, x, height]);
  for (let y = size; y < height; y += size) segments.push([0, y, width, y]);
  return segments;
}

/** Each hex gives its upper-left, upper-right and left edges, with the bottom row and right column closed, so shared edges are drawn once. */
function hexSegments(size: number, width: number, height: number): Segment[] {
  const segments: Segment[] = [];
  const radius = size / Math.sqrt(3);
  const rowStep = radius * 1.5;
  const columns = Math.ceil(width / size) + 1;
  const rows = Math.ceil(height / rowStep) + 1;
  if (columns * rows > MAX_GRID_CELLS) return segments;
  const edge = (a: readonly number[], b: readonly number[]): Segment => [a[0]!, a[1]!, b[0]!, b[1]!];
  for (let row = 0; row < rows; row++) {
    const cy = row * rowStep;
    const offset = row % 2 === 1 ? size / 2 : 0;
    for (let column = 0; column < columns; column++) {
      const c = hexCorners(column * size + offset, cy, radius);
      segments.push(edge(c[5]!, c[0]!), edge(c[0]!, c[1]!), edge(c[4]!, c[5]!));
      if (row === rows - 1) segments.push(edge(c[2]!, c[3]!), edge(c[3]!, c[4]!));
      if (column === columns - 1) segments.push(edge(c[1]!, c[2]!));
    }
  }
  return segments;
}

/** Corners from the top, clockwise. */
export function hexCorners(cx: number, cy: number, radius: number): [number, number][] {
  return Array.from({ length: 6 }, (_, i) => {
    const angle = ((-90 + 60 * i) * Math.PI) / 180;
    return [cx + radius * Math.cos(angle), cy + radius * Math.sin(angle)];
  });
}

// ---- saving ----

/** `MapScale` and `MapGrid`'s own tidying, then saved (`AtlasService.updateMapSettings`). */
export function updateMapSettings(conn: SqlConnection, mapId: string, scale: MapScale, grid: MapGrid, now: string = new Date().toISOString()): void {
  if (!['NONE', 'SQUARE', 'HEX'].includes(grid.type)) throw new EntryEditError('invalid', 'That is not a kind of grid.');
  if (!/^#[0-9A-Fa-f]{6}$/.test(grid.colorHex)) throw new EntryEditError('invalid', 'That is not a colour.');
  if (scale.unit.length > 40) throw new EntryEditError('invalid', 'A unit can be at most 40 characters.');
  const unitsPerPixel = Number.isFinite(scale.unitsPerPixel) && scale.unitsPerPixel > 0 ? scale.unitsPerPixel : 0;
  const cellSize = Number.isFinite(grid.cellSize) ? Math.min(Math.max(MIN_CELL_SIZE, grid.cellSize), 100_000) : 50;
  const opacity = Number.isFinite(grid.opacity) ? Math.max(0, Math.min(1, grid.opacity)) : 0.3;
  transaction(conn, () => {
    if (int(scalar(conn, 'SELECT COUNT(*) FROM atlas_maps WHERE id = ?', [mapId])) === 0) throw new EntryEditError('not_found', 'That map no longer exists.');
    conn.run('UPDATE atlas_maps SET scale_units_per_pixel = ?, scale_unit = ?, grid_type = ?, grid_size = ?, grid_color = ?, grid_opacity = ?, modified = ? WHERE id = ?', [
      unitsPerPixel,
      scale.unit.trim(),
      grid.type,
      cellSize,
      grid.colorHex,
      opacity,
      now,
      mapId,
    ]);
  });
}

/** What a new map starts with (Settings > New things start with): a grid kind and a scale unit. */
export interface NewMapDefaults {
  readonly gridType: GridType;
  readonly unit: string;
}
